const CollectionVendorMix = (() => {
  const MAX_PRODUCTS = 5000;
  const BUFFERED_THRESHOLD = 500;

  const getVendorKey = el =>
    (
      el?.dataset?.vendorHandle ||
      el?.dataset?.vendor ||
      el
        ?.querySelector('.card-product__vendor')
        ?.textContent?.trim()
        ?.toLowerCase() ||
      ''
    )
      .trim()
      .toLowerCase();

  const isProductCard = el =>
    el &&
    el.tagName &&
    el.tagName.toLowerCase() === 'card-product' &&
    !el.classList.contains('js-card-text');

  const isEnabled = root => {
    const container =
      root?.querySelector?.('#ProductGridContainer') ||
      document.querySelector('#ProductGridContainer');
    if (!container) return false;
    const v = container.getAttribute('data-mixed-product-collection');
    return v === 'true' || v === '1';
  };

  const normalizeUrlForState = href => {
    const url = new URL(href, window.location.origin);
    url.searchParams.delete('page');
    const params = Array.from(url.searchParams.entries()).sort((a, b) =>
      `${a[0]}=${a[1]}`.localeCompare(`${b[0]}=${b[1]}`)
    );
    url.search = '';
    for (const [k, v] of params) url.searchParams.append(k, v);
    return url.toString();
  };

  const buildPageUrl = page => {
    const url = new URL(window.location.href);
    if (page <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(page));
    }
    return url.toString();
  };

  const getCurrentPage = () => {
    const url = new URL(window.location.href);
    const v = parseInt(url.searchParams.get('page') || '1', 10);
    return Number.isFinite(v) && v > 0 ? v : 1;
  };

  const getTotalCountFromDom = root => {
    const scope = root?.querySelector ? root : document;
    const texts = [
      scope.querySelector('#ProductCount')?.textContent,
      scope.querySelector('#ProductCountMobile')?.textContent,
    ]
      .filter(Boolean)
      .map(s => String(s).toLowerCase());

    for (const t of texts) {
      const m = t.match(/(\d[\d,]*)/);
      if (!m) continue;
      const n = parseInt(m[1].replace(/,/g, ''), 10);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };

  const getGrid = root =>
    root?.querySelector?.('#product-grid') ||
    document.querySelector('#product-grid');

  const getContainer = root =>
    root?.querySelector?.('#ProductGridContainer') ||
    document.querySelector('#ProductGridContainer');

  const clearSkeleton = root => {
    const grid = getGrid(root);
    if (!grid) return;
    for (const el of Array.from(grid.querySelectorAll(':scope > .vendor-mix-skeleton__item'))) {
      el.remove();
    }
  };

  const ensureSkeleton = root => {
    const grid = getGrid(root);
    if (!grid) return;
    if (!document.getElementById('vendor-mix-skeleton-style')) {
      const style = document.createElement('style');
      style.id = 'vendor-mix-skeleton-style';
      style.textContent =
        '@keyframes vendorMixSkeletonShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}';
      document.head.appendChild(style);
    }

    const applyInlineSkeletonStyles = items => {
      if (!items || items.length === 0) return;
      const minHeight = window.matchMedia('(max-width: 749px)').matches
        ? '27.2rem'
        : '32rem';
      for (const item of items) {
        item.style.minHeight = minHeight;
        item.style.borderRadius = '0.4rem';
        item.style.border = '0.1rem solid rgba(var(--color-foreground), 0.06)';
        item.style.background =
          'linear-gradient(90deg, rgba(var(--color-foreground), 0.045) 0%, rgba(var(--color-foreground), 0.075) 50%, rgba(var(--color-foreground), 0.045) 100%)';
        item.style.backgroundSize = '260% 100%';
        item.style.animation = 'vendorMixSkeletonShimmer 2.4s ease-in-out infinite';
      }
    };

    const existingItems = Array.from(
      grid.querySelectorAll(':scope > .vendor-mix-skeleton__item')
    );
    if (existingItems.length) {
      applyInlineSkeletonStyles(existingItems);
      return;
    }

    const container = getContainer(root);
    const perPageRaw = container?.getAttribute?.('data-products-per-page') || '';
    const perPage = parseInt(perPageRaw, 10);
    const count = Number.isFinite(perPage) && perPage > 0 ? perPage : 24;

    const created = [];
    for (let i = 0; i < count; i++) {
      const item = document.createElement('div');
      item.className = 'vendor-mix-skeleton__item';
      item.setAttribute('aria-hidden', 'true');
      item.textContent = '\u200B';
      created.push(item);
    }

    applyInlineSkeletonStyles(created);
    for (let i = created.length - 1; i >= 0; i--) {
      grid.insertBefore(created[i], grid.firstChild);
    }
  };

  const setLoading = (root, loading) => {
    const container = getContainer(root);
    if (!container) return;
    if (loading) {
      ensureSkeleton(root);
      container.classList.add('vendor-mix-loading');
      return;
    }

    clearSkeleton(root);
    container.classList.remove('vendor-mix-loading');
  };

  const getLoadMoreButton = root =>
    (
      root?.querySelector?.('collection-load-more .js-btn-load-more') ||
      document.querySelector('collection-load-more .js-btn-load-more')
    )?.closest?.('.js-btn-load-more') ||
    root?.querySelector?.('collection-load-more .js-btn-load-more') ||
    document.querySelector('collection-load-more .js-btn-load-more');

  const getLoadPreviousButton = root =>
    (
      root?.querySelector?.('collection-load-previous .js-btn-load-previous') ||
      document.querySelector('collection-load-previous .js-btn-load-previous')
    )?.closest?.('.js-btn-load-previous') ||
    root?.querySelector?.('collection-load-previous .js-btn-load-previous') ||
    document.querySelector('collection-load-previous .js-btn-load-previous');

  const getLoadMoreContainer = root =>
    root?.querySelector?.('collection-load-more') ||
    document.querySelector('collection-load-more');

  const getLoadPreviousContainer = root =>
    root?.querySelector?.('collection-load-previous') ||
    document.querySelector('collection-load-previous');

  const mixFullDataset = products => {
    const buckets = new Map();
    const vendorOrder = [];

    for (const p of products) {
      if (!p.vendor) return products.slice();
      if (!buckets.has(p.vendor)) {
        buckets.set(p.vendor, []);
        vendorOrder.push(p.vendor);
      }
      buckets.get(p.vendor).push(p);
    }

    if (vendorOrder.length <= 1) return products.slice();

    const active = vendorOrder.filter(k => (buckets.get(k) || []).length > 0);
    const out = [];
    let prevVendor = null;
    let pointer = 0;

    while (active.length >= 2) {
      let progressed = false;
      for (let i = 0; i < active.length; i++) {
        const idx = (pointer + i) % active.length;
        const vendor = active[idx];
        if (vendor === prevVendor) continue;
        const bucket = buckets.get(vendor);
        if (!bucket || bucket.length === 0) continue;
        out.push(bucket.shift());
        prevVendor = vendor;
        progressed = true;
        if (bucket.length === 0) {
          active.splice(idx, 1);
          pointer = idx % Math.max(active.length, 1);
        } else {
          pointer = (idx + 1) % active.length;
        }
        break;
      }
      if (!progressed) break;
    }

    for (const vendor of vendorOrder) {
      const bucket = buckets.get(vendor);
      if (!bucket || bucket.length === 0) continue;
      out.push(...bucket.splice(0, bucket.length));
    }

    return out.length ? out : products.slice();
  };

  const fetchAllProductsForState = async stateKey => {
    const base = new URL(window.location.href);
    base.searchParams.delete('page');

    const products = [];
    const visited = new Set();
    let nextUrl = base.toString();

    while (nextUrl && !visited.has(nextUrl)) {
      visited.add(nextUrl);
      const res = await fetch(nextUrl, { credentials: 'same-origin' });
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const grid = doc.querySelector('#product-grid');
      if (!grid) break;

      const items = Array.from(grid.children).filter(isProductCard);
      for (const el of items) {
        const vendor = getVendorKey(el);
        if (!vendor) return null;
        products.push({ vendor, html: el.outerHTML });
        if (products.length >= MAX_PRODUCTS) break;
      }
      if (products.length >= MAX_PRODUCTS) break;

      const btn = doc.querySelector('collection-load-more .js-btn-load-more');
      const href = btn?.dataset?.href;
      if (!href) break;
      const abs = new URL(href, nextUrl).toString();
      if (normalizeUrlForState(abs) !== stateKey) break;
      nextUrl = abs;
    }

    return products;
  };

  const state = {
    key: null,
    preparing: null,
    ready: false,
    mode: 'full',
    products: [],
    mixed: [],
    pageSize: 0,
    totalPages: 1,
    totalProducts: 0,
    currentPage: 1,
    baseUrl: null,
    nextFetchUrl: null,
    visited: null,
    buckets: null,
    vendorOrder: null,
    pointer: 0,
    prevVendor: null,
    hasMore: false,
  };

  const updatePaginationUI = root => {
    const loadMoreContainer = getLoadMoreContainer(root);
    const loadPreviousContainer = getLoadPreviousContainer(root);
    const loadMoreBtn = getLoadMoreButton(root);
    const loadPreviousBtn = getLoadPreviousButton(root);

    if (loadMoreBtn) loadMoreBtn.dataset.href = buildPageUrl(state.currentPage + 1);
    if (loadPreviousBtn) loadPreviousBtn.dataset.href = buildPageUrl(state.currentPage - 1);

    if (loadMoreContainer) {
      const isLast =
        state.mode === 'full'
          ? state.currentPage >= state.totalPages
          : !state.hasMore && state.currentPage >= state.totalPages;
      isLast ? loadMoreContainer.classList.add('hidden') : loadMoreContainer.classList.remove('hidden');
    }

    if (loadPreviousContainer) {
      state.currentPage <= 1
        ? (loadPreviousContainer.style.display = 'none')
        : (loadPreviousContainer.style.display = '');
    }
  };

  const resetBufferedState = () => {
    const base = new URL(window.location.href);
    base.searchParams.delete('page');
    state.baseUrl = base.toString();
    state.nextFetchUrl = state.baseUrl;
    state.visited = new Set();
    state.buckets = new Map();
    state.vendorOrder = [];
    state.pointer = 0;
    state.prevVendor = null;
    state.hasMore = true;
    state.totalProducts = 0;
    state.totalPages = 1;
    state.products = [];
    state.mixed = [];
  };

  const bufferedAddProductsFromDoc = doc => {
    const grid = doc.querySelector('#product-grid');
    if (!grid) return false;

    const items = Array.from(grid.children).filter(isProductCard);
    for (const el of items) {
      const vendor = getVendorKey(el);
      if (!vendor) return false;
      const p = { vendor, html: el.outerHTML };
      state.products.push(p);
      if (!state.buckets.has(vendor)) {
        state.buckets.set(vendor, []);
        state.vendorOrder.push(vendor);
      }
      state.buckets.get(vendor).push(p);
      if (state.products.length >= MAX_PRODUCTS) break;
    }

    return true;
  };

  const bufferedUpdateNextUrlFromDoc = (doc, currentUrl) => {
    const btn = doc.querySelector('collection-load-more .js-btn-load-more');
    const href = btn?.dataset?.href;
    if (!href) {
      state.hasMore = false;
      state.nextFetchUrl = null;
      return;
    }
    const abs = new URL(href, currentUrl).toString();
    if (normalizeUrlForState(abs) !== state.key) {
      state.hasMore = false;
      state.nextFetchUrl = null;
      return;
    }
    state.nextFetchUrl = abs;
    state.hasMore = true;
  };

  const bufferedFetchNextPage = async () => {
    if (!state.hasMore || !state.nextFetchUrl) return false;
    if (state.products.length >= MAX_PRODUCTS) {
      state.hasMore = false;
      state.nextFetchUrl = null;
      return false;
    }

    const nextUrl = state.nextFetchUrl;
    if (state.visited.has(nextUrl)) {
      state.hasMore = false;
      state.nextFetchUrl = null;
      return false;
    }
    state.visited.add(nextUrl);

    const res = await fetch(nextUrl, { credentials: 'same-origin' });
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const ok = bufferedAddProductsFromDoc(doc);
    if (!ok) return false;
    bufferedUpdateNextUrlFromDoc(doc, nextUrl);

    if (!state.hasMore) {
      state.totalProducts = state.products.length;
      state.totalPages = Math.max(1, Math.ceil(state.totalProducts / state.pageSize));
    }

    return true;
  };

  const bufferedGenerateNext = async () => {
    while (true) {
      const active = state.vendorOrder.filter(v => (state.buckets.get(v) || []).length > 0);
      if (active.length === 0) {
        if (state.hasMore) {
          const fetched = await bufferedFetchNextPage();
          if (!fetched) return null;
          continue;
        }
        return null;
      }

      if (active.length === 1) {
        if (state.hasMore) {
          const fetched = await bufferedFetchNextPage();
          if (!fetched) {
            const v = active[0];
            const b = state.buckets.get(v) || [];
            if (b.length === 0) return null;
            const p = b.shift();
            state.prevVendor = v;
            return p;
          }
          continue;
        }

        const v = active[0];
        const b = state.buckets.get(v) || [];
        if (b.length === 0) return null;
        const p = b.shift();
        state.prevVendor = v;
        return p;
      }

      const start = state.pointer % active.length;
      let chosenIdx = -1;
      for (let i = 0; i < active.length; i++) {
        const idx = (start + i) % active.length;
        if (active[idx] === state.prevVendor) continue;
        chosenIdx = idx;
        break;
      }
      if (chosenIdx === -1) chosenIdx = start;

      const vendor = active[chosenIdx];
      const bucket = state.buckets.get(vendor);
      if (!bucket || bucket.length === 0) {
        state.pointer = (chosenIdx + 1) % active.length;
        continue;
      }

      const p = bucket.shift();
      state.prevVendor = vendor;
      state.pointer = (chosenIdx + 1) % active.length;
      return p;
    }
  };

  const bufferedEnsureMixedLength = async targetLen => {
    while (state.mixed.length < targetLen) {
      const next = await bufferedGenerateNext();
      if (!next) break;
      state.mixed.push(next);
    }
    return state.mixed.length >= targetLen;
  };

  const createNodeFromHtml = html => {
    const t = document.createElement('template');
    t.innerHTML = html.trim();
    return t.content.firstElementChild;
  };

  const renderSlice = (root, { page, mode }) => {
    const grid = getGrid(root);
    if (!grid) return;

    const start = (page - 1) * state.pageSize;
    const end = Math.min(start + state.pageSize, state.mixed.length);
    const slice = state.mixed.slice(start, end).map(p => p.html);

    if (mode === 'append') {
      for (const html of slice) grid.appendChild(createNodeFromHtml(html));
      return;
    }

    if (mode === 'prepend') {
      for (let i = slice.length - 1; i >= 0; i--) {
        grid.insertBefore(createNodeFromHtml(slice[i]), grid.firstChild);
      }
      return;
    }

    const currentItems = Array.from(grid.children);
    const nonProduct = currentItems
      .map((el, index) => ({ el, index }))
      .filter(x => !isProductCard(x.el));

    const desiredLength = slice.length + nonProduct.length;
    const slots = new Array(desiredLength).fill(null);

    for (const x of nonProduct) {
      let i = Math.min(x.index, desiredLength - 1);
      while (i < desiredLength && slots[i]) i++;
      if (i >= desiredLength) {
        i = desiredLength - 1;
        while (i >= 0 && slots[i]) i--;
      }
      if (i >= 0) slots[i] = x.el;
    }

    let p = 0;
    for (let i = 0; i < slots.length; i++) {
      if (slots[i]) continue;
      if (p >= slice.length) break;
      slots[i] = createNodeFromHtml(slice[p++]);
    }

    grid.innerHTML = '';
    for (const el of slots) {
      if (el) grid.appendChild(el);
    }
  };

  const prepare = async root => {
    if (!isEnabled(root)) return false;

    const key = normalizeUrlForState(window.location.href);
    if (state.ready && state.key === key) return true;
    if (state.preparing && state.key === key) return state.preparing;

    state.key = key;
    state.ready = false;
    state.mode = 'full';
    state.products = [];
    state.mixed = [];
    state.totalProducts = 0;
    state.totalPages = 1;
    state.hasMore = false;

    const grid = getGrid(root);
    if (!grid) return false;

    setLoading(root, true);

    const initialProducts = Array.from(grid.children).filter(isProductCard);
    const container = getContainer(root);
    const perPageRaw = container?.getAttribute?.('data-products-per-page') || '';
    const perPage = parseInt(perPageRaw, 10);
    state.pageSize =
      Number.isFinite(perPage) && perPage > 0
        ? perPage
        : Math.max(1, initialProducts.length);
    state.currentPage = getCurrentPage();

    state.preparing = (async () => {
      try {
        const total = getTotalCountFromDom(root);
        if (total && total > BUFFERED_THRESHOLD) {
          state.mode = 'buffered';
          resetBufferedState();
          await bufferedEnsureMixedLength(state.currentPage * state.pageSize);
          state.ready = true;
          updatePaginationUI(root);
          renderSlice(root, { page: state.currentPage, mode: 'replace' });
          setLoading(root, false);
          return true;
        }

        const products = await fetchAllProductsForState(key);
        if (!products || products.length === 0) {
          setLoading(root, false);
          return false;
        }

        state.products = products;
        state.totalProducts = products.length;
        state.mixed = mixFullDataset(products);
        state.totalPages = Math.max(1, Math.ceil(state.totalProducts / state.pageSize));
        if (state.currentPage > state.totalPages) state.currentPage = state.totalPages;
        state.hasMore = state.currentPage < state.totalPages;
        state.ready = true;
        updatePaginationUI(root);
        renderSlice(root, { page: state.currentPage, mode: 'replace' });
        setLoading(root, false);
        return true;
      } catch (_) {
        setLoading(root, false);
        return false;
      }
    })();

    return state.preparing;
  };

  const handleLoadMore = async root => {
    await prepare(root);
    if (!state.ready) return false;
    if (state.mode === 'full' && state.currentPage >= state.totalPages) return true;
    if (state.mode === 'buffered' && !state.hasMore && state.currentPage >= state.totalPages) return true;

    const nextPage = state.currentPage + 1;
    if (state.mode === 'buffered') {
      setLoading(root, true);
      await bufferedEnsureMixedLength(nextPage * state.pageSize);
      setLoading(root, false);
      if (!state.hasMore && state.totalProducts) {
        state.totalPages = Math.max(1, Math.ceil(state.totalProducts / state.pageSize));
      }
    }
    renderSlice(root, { page: nextPage, mode: 'append' });
    state.currentPage = nextPage;
    updatePaginationUI(root);
    history.replaceState({}, '', buildPageUrl(state.currentPage));
    document.dispatchEvent(
      new CustomEvent('collection:grid-updated', {
        bubbles: true,
        detail: { source: 'load-more' },
      })
    );
    return true;
  };

  const handleLoadPrevious = async root => {
    await prepare(root);
    if (!state.ready) return false;
    if (state.currentPage <= 1) return true;

    const prevPage = state.currentPage - 1;
    renderSlice(root, { page: prevPage, mode: 'prepend' });
    state.currentPage = prevPage;
    updatePaginationUI(root);
    history.replaceState({}, '', buildPageUrl(state.currentPage));
    document.dispatchEvent(
      new CustomEvent('collection:grid-updated', {
        bubbles: true,
        detail: { source: 'load-previous' },
      })
    );
    return true;
  };

  const init = () => {
    if (!isEnabled(document)) return;
    prepare(document);
    document.addEventListener('collection:grid-updated', e => {
      const source = e?.detail?.source;
      if (source === 'facets') {
        state.ready = false;
        state.preparing = null;
        prepare(document);
      }
    });
    window.addEventListener('shopify:section:load', () => {
      state.ready = false;
      state.preparing = null;
      prepare(document);
    });
  };

  return { init, handleLoadMore, handleLoadPrevious };
})();

window.CollectionVendorMix = CollectionVendorMix;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => CollectionVendorMix.init());
} else {
  CollectionVendorMix.init();
}
